import { useMutation, useQuery } from '@apollo/client';
import { useFindServicePackageForStoreQuery } from '@/graphql/queries/findServicePackageForStore.generated';
// @ts-ignore
import nestedProperty from 'nested-property';
import {
  adminInfoStoreQuery,
  productCategories as productCategoriesQuery,
} from '../../graphql/queries';
import { useContext, useState } from 'react';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { getObjectDiff } from '@/utils/objDiff';
import { isContractEnding } from '@/utils/contractEndDate';
import { LayoutStoreInstance } from '../../LayoutStore';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { ContentSection } from '../../components/Layout/ContentSection';
import {
  Button,
  Alert,
  InputGroupText,
  Input,
  InputGroup,
  Card,
  CardBody,
  Badge,
  Row,
  Col,
} from 'reactstrap';
import {
  FaCalendarAlt,
  FaUser,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUnlock,
  FaInfoCircle,
} from 'react-icons/fa';
import { Store, TerminationReason } from '../../graphql/generatedTypes';
import { ThemeContext } from '../../providers/ThemeContextProvider';
import { Link } from 'react-router-dom';
import { Routes } from '@/utils/routes';

// colugo's updateStoreSuperAdmin rejects more: Zoho's termination comment fields hold 255 characters.
const TERMINATION_COMMENT_MAX_LENGTH = 255;

type MerchantContractDataTabProps = {
  externalPickpackId: string | null;
  title: string;
};

export const MerchantContractDataTab = ({
  externalPickpackId,
  title,
}: MerchantContractDataTabProps) => {
  const { isDarkMode } = useContext(ThemeContext);

  const [storeUpdate, setStoreUpdate] = useState<any | undefined>(null);
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>(null);
  const [updateStoreSuperAdmin, { loading: updatingSuperAdminInfo }] =
    useMutation(updateStoreSuperAdminMutation);

  const viewer = useContext(ViewerContext);

  const allowedToAccess = CanAccessPartnerData(viewer);

  const getContractStatus = () => {
    if (!storeUpdate) return null;
    const now = new Date();
    const contractEnd = storeUpdate.contract_end_date
      ? new Date(storeUpdate.contract_end_date)
      : null;
    const testPeriodEnd = storeUpdate.test_period_end
      ? new Date(storeUpdate.test_period_end)
      : null;
    const onboardedDate = storeUpdate.onboarded_date
      ? new Date(storeUpdate.onboarded_date)
      : null;

    if (testPeriodEnd && now < testPeriodEnd) {
      return {
        type: 'warning',
        text: i18n.t('Common:testPeriod'),
        icon: <FaClock />,
      };
    }
    if (contractEnd && now > contractEnd) {
      return {
        type: 'danger',
        text: i18n.t('Common:expired'),
        icon: <FaExclamationTriangle />,
      };
    }
    if (onboardedDate) {
      return {
        type: 'success',
        text: i18n.t('Common:active'),
        icon: <FaCheckCircle />,
      };
    }
    return {
      type: 'secondary',
      text: i18n.t('Common:pending'),
      icon: <FaClock />,
    };
  };

  const formatDateDisplay = (dateString: string | null, label: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const isOverdue = date < now;

    return (
      <div
        className={`small ${
          isOverdue
            ? 'text-danger'
            : isDarkMode
              ? 'text-light opacity-75'
              : 'text-muted'
        }`}
      >
        <FaInfoCircle className="me-1" />
        {label}:{' '}
        {date.toLocaleDateString(i18n.language, getDateFormatOptions(true))}
        {isOverdue && (
          <span className="ms-1">({i18n.t('Common:overdue')})</span>
        )}
      </div>
    );
  };

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const {
    data = { store: null },
    loading: loadingStore,
    refetch,
  } = useQuery(adminInfoStoreQuery, {
    fetchPolicy: 'no-cache',
    skip: !externalPickpackId,
    variables: { id: externalPickpackId },
    onCompleted: (storeData) => {
      // @ts-ignore
      if (storeData) {
        resetStore(storeData.store);
      }
    },
  });

  // Must be called before any early returns to satisfy Rules of Hooks
  const { data: spData } = useFindServicePackageForStoreQuery({
    variables: { store_uuid: storeUpdate?.external_vytal_id ?? '' },
    skip: !storeUpdate?.external_vytal_id,
  });

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store = data.store as Store;

  function refuseContractEndDateClear(target: HTMLInputElement) {
    LayoutStoreInstance.setWarning(
      i18n.t('Contract:contractEndDateRequiredForContractEnding'),
    );
    // The input is uncontrolled (defaultValue), so put back what it was showing
    // — otherwise it sits visually empty while state keeps the old date.
    target.value = DateTimeHelpers.toDateInputFromUtcString(
      storeUpdate?.contract_end_date,
    );
  }

  // Refuse the clear the moment it happens instead of waiting for the field to
  // lose focus. React's onChange is the DOM input event, and a date input with
  // half-typed segments also reports an empty value — validity.badInput tells
  // that apart from a real clear, so entering a new date is never fought.
  function handleContractEndDateInput({
    target,
  }: React.ChangeEvent<HTMLInputElement>) {
    if (target.value !== '' || target.validity.badInput) return;
    if (!isContractEnding(storeUpdate?.onboarding_status)) return;
    refuseContractEndDateClear(target);
  }

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      const date = (target as EventTarget & HTMLInputElement).valueAsDate;
      // A cleared date must serialize to null. It used to become
      // toDateTimeOffsetString(new Date(0)), which is why so many rows carry a
      // 1970-01-01 sentinel that reads as "set" everywhere downstream.
      formattedValue =
        date != null ? DateTimeHelpers.toDateTimeOffsetString(date) : null;
    }

    // An Offboarding or Terminated store must keep a real contract end date.
    // Refuse the clear here too, not just on input, for anything that reaches
    // blur without an input event (half-typed segments, autofill).
    if (
      name === 'contract_end_date' &&
      formattedValue == null &&
      isContractEnding(storeUpdate?.onboarding_status)
    ) {
      refuseContractEndDateClear(target as HTMLInputElement);
      return;
    }

    setStoreUpdate((oldStoreUpdate: any) => {
      const newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, formattedValue);
      return newStoreUpdate;
    });
  }

  function isExpandedAccessActive(): boolean {
    if (!store.temporary_access_from) return false;
    const accessStart = new Date(store.temporary_access_from);
    const now = new Date();
    const accessEnd = new Date(accessStart.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days later
    return accessStart <= now && now <= accessEnd;
  }

  function getExpandedAccessEndDate(): Date | null {
    if (!store.temporary_access_from) return null;
    const accessStart = new Date(store.temporary_access_from);
    return new Date(accessStart.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days later
  }

  function handleClearExpandedAccess() {
    if (window.confirm(i18n.t('Contract:clearExpandedAccess'))) {
      setStoreUpdate((oldStoreUpdate: any) => ({
        ...oldStoreUpdate,
        temporary_access_from: null,
      }));
    }
  }

  function handleSaveSuperAdmin() {
    const getMutationInput = (storeIn: any) => ({
      type: storeIn.type,
      invoice_headline: storeIn.invoice_headline,
      onboarding_fee_vytal: isNaN(parseInt(storeIn.onboarding_fee_vytal))
        ? null
        : parseInt(storeIn.onboarding_fee_vytal),
      invoice_email: (storeIn.invoice_email || '').trim() || null,
      legal_name: (storeIn.legal_name || '').trim() || null,
      invoice_address: {
        ...(storeIn.invoice_address || {}),
        __typename: undefined,
      },
      custom_invoice_items: storeIn.custom_invoice_items
        ? storeIn.custom_invoice_items.map((item: object) => ({
            ...item,
            __typename: undefined,
          }))
        : null,
      contracted_date: storeIn.contracted_date,
      contract_end_date: storeIn.contract_end_date,
      onboarded_date: storeIn.onboarded_date,
      test_period_end: storeIn.test_period_end,
      temporary_access_from: storeIn.temporary_access_from,
      termination_reason: storeIn.termination_reason || null,
      termination_comment: (storeIn.termination_comment || '').trim() || null,
      coming_soon: storeIn.coming_soon,
      temporarily_unavailable: storeIn.temporarily_unavailable,
      published: storeIn.published,
      can_preorder: storeIn.can_preorder,
      can_deliver: storeIn.can_deliver,
      always_online: storeIn.always_online,
      phone_from_vytal: storeIn.phone_from_vytal,
      sim_info: storeIn.sim_info,
      sim_phone_number: storeIn.sim_phone_number,
      sim_payment_automation: storeIn.sim_payment_automation,
      show_in_stats: storeIn.show_in_stats,
      region: parseInt(storeIn.region),
    });
    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput admin', mutationInput);

    updateStoreSuperAdmin({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (_, { data: { updateStoreSuperAdmin: updatedStore } }) => {
        resetStore(updatedStore);
        refetch();
      },
    })
      .then(() => {
        // A save used to be entirely silent on success, so a save that changed
        // nothing was indistinguishable from one that did.
        LayoutStoreInstance.setSuccess(i18n.t('Contract:storeUpdated'));
      })
      .catch((err: any) => {
        alert(i18n.t('Contract:errorUpdatingStore') + ': ' + err);
      });
  }

  if (!externalPickpackId)
    return <p>{i18n.t('Contract:externalIdNotAvailable')}</p>;

  if (!store)
    return (
      <p>
        {i18n.t('Contract:storeCouldNotBeLoaded')}: {externalPickpackId}
      </p>
    );

  if (!allowedToAccess) {
    return null;
  }

  const contractStatus = getContractStatus();

  // ── Service package contract validation ──────────────────────────────────
  const assignedPackage = spData?.findServicePackageForStore ?? null;

  // Minimum term from the single active revision of the assigned ServicePackageTerm
  const minTermMonths =
    assignedPackage?.revision?.min_contract_term_months ?? null;

  const servicePackageStartDate = storeUpdate?.service_package_start_date;
  const hasServicePackageStartDate = !!servicePackageStartDate;

  // Earliest allowed contract end date = service_package_start_date + min term
  const earliestAllowedEndDate: Date | null = (() => {
    if (!assignedPackage || !hasServicePackageStartDate || !minTermMonths)
      return null;
    const d = new Date(servicePackageStartDate);
    d.setMonth(d.getMonth() + minTermMonths);
    return d;
  })();

  const currentContractEnd = storeUpdate?.contract_end_date
    ? new Date(storeUpdate.contract_end_date)
    : null;

  const contractEndTooEarly =
    earliestAllowedEndDate !== null &&
    currentContractEnd !== null &&
    currentContractEnd < earliestAllowedEndDate;

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={i18n.t('Contract:contractData')}
        isLoading={updatingSuperAdminInfo}
        onSave={handleSaveSuperAdmin}
      >
        {contractStatus && (
          <Card
            className={`mb-4 border-0 shadow-sm ${
              isDarkMode ? 'bg-dark text-light' : 'bg-white'
            }`}
          >
            <CardBody>
              <Row className="align-items-center">
                <Col xs="auto">
                  <div className={`text-${contractStatus.type} fs-2`}>
                    {contractStatus.icon}
                  </div>
                </Col>
                <Col>
                  <h5 className="mb-1">
                    <Badge color={contractStatus.type} className="me-2">
                      {contractStatus.text}
                    </Badge>
                    <span className={isDarkMode ? 'text-light' : 'text-dark'}>
                      {i18n.t('Contract:contractStatus')}
                    </span>
                  </h5>
                  <div
                    className={
                      isDarkMode ? 'text-light opacity-75' : 'text-muted'
                    }
                  >
                    {formatDateDisplay(
                      storeUpdate.contract_end_date,
                      i18n.t('Contract:ends'),
                    )}
                    {formatDateDisplay(
                      storeUpdate.test_period_end,
                      i18n.t('Contract:testPeriodEnds'),
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        <EditItem title={i18n.t('Contract:contractInformation')}>
          <Row>
            <Col md={6}>
              <Card
                className={`h-100 border-0 ${
                  isDarkMode ? 'bg-dark' : 'bg-light'
                }`}
              >
                <CardBody>
                  <h6
                    className={`${
                      isDarkMode ? 'text-light opacity-75' : 'text-muted'
                    } mb-3`}
                  >
                    <FaCalendarAlt className="me-2" />
                    {i18n.t('Contract:contractDates')}
                  </h6>

                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:contractedDate')}
                    </label>
                    <Input
                      type="date"
                      data-isdate
                      name="contracted_date"
                      onBlur={handleChange}
                      // Uncontrolled (defaultValue), so React never repaints the
                      // DOM value on its own. Keying on the value re-mounts the
                      // input whenever state changes, so what is displayed
                      // always matches what will be saved — a save that did not
                      // change this field can no longer leave it looking empty.
                      key={`contracted_date-${storeUpdate.contracted_date ?? 'empty'}`}
                      defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.contracted_date,
                      )}
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:contractEndDate')}
                    </label>
                    <Input
                      type="date"
                      data-isdate
                      name="contract_end_date"
                      onBlur={handleChange}
                      onChange={handleContractEndDateInput}
                      key={`contract_end_date-${storeUpdate.contract_end_date ?? 'empty'}`}
                      defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.contract_end_date,
                      )}
                      disabled={
                        !!assignedPackage && !hasServicePackageStartDate
                      }
                    />
                    <div
                      className={`form-text ${
                        isDarkMode ? 'text-light opacity-75' : ''
                      }`}
                    >
                      <FaInfoCircle className="me-1" />
                      {i18n.t('Contract:contractEndDateTitle')}
                    </div>
                    {/*
                      Stated up front rather than only after a refused clear, so
                      the constraint is knowable before the operator tries.
                    */}
                    {isContractEnding(storeUpdate.onboarding_status) && (
                      <div className="form-text text-warning mt-1">
                        <FaExclamationTriangle className="me-1" />
                        {i18n.t(
                          'Contract:contractEndDateRequiredForContractEnding',
                        )}
                      </div>
                    )}
                    {assignedPackage && !hasServicePackageStartDate && (
                      <div className="form-text text-warning mt-1">
                        <FaExclamationTriangle className="me-1" />
                        Contract end date cannot be set without a service
                        package start date when a service package is assigned.
                      </div>
                    )}
                    {contractEndTooEarly && earliestAllowedEndDate && (
                      <div className="form-text text-warning mt-1">
                        <FaExclamationTriangle className="me-1" />
                        Warning: end date is before the minimum contract term (
                        {minTermMonths} months). Earliest allowed:{' '}
                        {earliestAllowedEndDate.toLocaleDateString(
                          i18n.language,
                          getDateFormatOptions(true),
                        )}
                        .
                      </div>
                    )}
                  </div>

                  {hasServicePackageStartDate && (
                    <div className="mb-3">
                      <label
                        className={`form-label fw-semibold ${
                          isDarkMode ? 'text-light' : ''
                        }`}
                      >
                        Service package start date
                      </label>
                      <Input
                        type="date"
                        value={DateTimeHelpers.toDateInputFromUtcString(
                          servicePackageStartDate,
                        )}
                        disabled
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:onboardedDate')}
                    </label>
                    <Input
                      type="date"
                      name="onboarded_date"
                      data-isdate
                      onBlur={handleChange}
                      key={`onboarded_date-${storeUpdate.onboarded_date ?? 'empty'}`}
                      defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.onboarded_date,
                      )}
                    />
                  </div>

                  <div className="mb-0">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:testPeriodEnd')}
                    </label>
                    <Input
                      type="date"
                      name="test_period_end"
                      data-isdate
                      onChange={handleChange}
                      defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.test_period_end,
                      )}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card
                className={`h-100 border-0 ${
                  isDarkMode ? 'bg-dark' : 'bg-light'
                }`}
              >
                <CardBody>
                  <h6
                    className={`${
                      isDarkMode ? 'text-light opacity-75' : 'text-muted'
                    } mb-3`}
                  >
                    <FaUser className="me-2" />
                    {i18n.t('Contract:accountInformation')}
                  </h6>

                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:createdBy')}
                    </label>
                    {storeUpdate.created_by != null ? (
                      <div className="d-flex align-items-center">
                        <Badge color="primary" pill className="me-2">
                          <FaUser className="me-1" />
                          {i18n.t('Contract:userId')}
                        </Badge>
                        <Link
                          to={{
                            pathname: Routes.users(storeUpdate.created_by),
                            search: '?activeTab=1',
                          }}
                          className={`text-decoration-none fw-semibold ${
                            isDarkMode ? 'text-info' : 'text-primary'
                          }`}
                        >
                          {storeUpdate.created_by}
                        </Link>
                      </div>
                    ) : (
                      <Input
                        type="text"
                        disabled
                        name="created_by"
                        value={i18n.t('Common:unknown') as string}
                        className={
                          isDarkMode ? 'bg-dark text-light' : 'bg-white'
                        }
                      />
                    )}
                  </div>

                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:acquiredAt')}
                    </label>
                    <Input
                      type="text"
                      disabled
                      name="acquired_at"
                      value={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.acquired_at,
                      )}
                      className={isDarkMode ? 'bg-dark text-light' : 'bg-white'}
                    />
                  </div>

                  <div className="mb-0">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:acquiredBy')}
                    </label>
                    <Input
                      type="text"
                      disabled
                      name="acquired_by"
                      value={storeUpdate.acquired_by || ''}
                      className={isDarkMode ? 'bg-dark text-light' : 'bg-white'}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </EditItem>

        <EditItem title={i18n.t('Contract:termination')}>
          <Card
            className={`border-0 shadow-sm ${
              isDarkMode ? 'bg-dark text-light' : 'bg-white'
            }`}
          >
            <CardBody>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:terminationReason')}
                    </label>
                    <Input
                      type="select"
                      name="termination_reason"
                      onChange={handleChange}
                      value={storeUpdate.termination_reason || ''}
                    >
                      <option value="">-</option>
                      {Object.values(TerminationReason).map((reason) => (
                        <option key={reason} value={reason}>
                          {i18n.t('Contract:terminationReasons:' + reason)}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="mb-0">
                    <label
                      className={`form-label fw-semibold ${
                        isDarkMode ? 'text-light' : ''
                      }`}
                    >
                      {i18n.t('Contract:terminationComment')}
                    </label>
                    <Input
                      type="textarea"
                      rows={3}
                      name="termination_comment"
                      maxLength={TERMINATION_COMMENT_MAX_LENGTH}
                      onChange={handleChange}
                      value={storeUpdate.termination_comment || ''}
                    />
                    <small className="text-muted">
                      {(storeUpdate.termination_comment || '').length}/
                      {TERMINATION_COMMENT_MAX_LENGTH}
                    </small>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </EditItem>

        <EditItem title={i18n.t('Contract:expandedAccessWindow')}>
          <Card
            className={`border-0 shadow-sm ${
              isDarkMode ? 'bg-dark text-light' : 'bg-white'
            }`}
          >
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col xs="auto">
                  <div
                    className={`text-${
                      isExpandedAccessActive() ? 'success' : 'muted'
                    } fs-3`}
                  >
                    <FaUnlock />
                  </div>
                </Col>
                <Col>
                  <h6 className="mb-1">
                    {isExpandedAccessActive() ? (
                      <Badge color="success" className="me-2">
                        <FaCheckCircle className="me-1" />
                        {i18n.t('Common:active')}
                      </Badge>
                    ) : (
                      <Badge color="secondary" className="me-2">
                        {i18n.t('Contract:inactive')}
                      </Badge>
                    )}
                    <span className={isDarkMode ? 'text-light' : 'text-dark'}>
                      {i18n.t('Contract:expandedAccessControl')}
                    </span>
                  </h6>
                  <div
                    className={`${
                      isDarkMode ? 'text-light opacity-75' : 'text-muted'
                    } small`}
                  >
                    {i18n.t('Contract:expandedAccessDescription')}
                  </div>
                </Col>
              </Row>

              {isExpandedAccessActive() && (
                <Alert color="success" className="mb-3 border-0">
                  <div className="d-flex align-items-center">
                    <FaCheckCircle className="me-2" />
                    <div>
                      <strong>{i18n.t('Contract:expandedAccessActive')}</strong>
                      <br />
                      <small>
                        {i18n.t('Contract:expires')}:{' '}
                        {getExpandedAccessEndDate()?.toLocaleDateString(
                          i18n.language,
                          getDateFormatOptions(true),
                        )}
                      </small>
                    </div>
                  </div>
                </Alert>
              )}

              <Row>
                <Col md={8}>
                  <label
                    className={`form-label fw-semibold ${
                      isDarkMode ? 'text-light' : ''
                    }`}
                  >
                    {i18n.t('Contract:setExpandedAccess')}
                  </label>
                  <InputGroup>
                    <InputGroupText>
                      <FaCalendarAlt />
                    </InputGroupText>
                    <Input
                      type="date"
                      data-isdate
                      name="temporary_access_from"
                      onBlur={handleChange}
                      // Also re-mounts when "Clear expanded access" nulls the
                      // value, which otherwise left the old date on screen.
                      key={`temporary_access_from-${storeUpdate.temporary_access_from ?? 'empty'}`}
                      defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                        storeUpdate.temporary_access_from,
                      )}
                    />
                    <Button
                      color="outline-danger"
                      onClick={handleClearExpandedAccess}
                      disabled={!storeUpdate.temporary_access_from}
                    >
                      {i18n.t('Contract:clearExpandedAccess')}
                    </Button>
                  </InputGroup>
                </Col>
              </Row>

              {store.temporary_access_from && (
                <div
                  className={`mt-3 p-3 ${
                    isDarkMode ? 'bg-secondary' : 'bg-light'
                  } rounded`}
                >
                  <div
                    className={`${
                      isDarkMode ? 'text-light opacity-75' : 'text-muted'
                    } small`}
                  >
                    <strong>
                      <FaInfoCircle className="me-1" />
                      {i18n.t('Contract:accessStarted')}:
                    </strong>{' '}
                    {new Date(store.temporary_access_from).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(true),
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
